import React, { useState, useEffect, useRef, useContext, memo, useCallback, useMemo } from 'react';
import useClickAndDragScroll from '../hooks/useClickAndDragScroll';
import axios from 'axios';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import socketService from '../services/socket';
import { AuthContext } from '../context/AuthContext';
import Navbar from './Navbar';
import BucketNavbar from './BucketNavbar';
import AddTicketModal from './AddTicketModal';
import TicketDetailsModal from './TicketDetailsModal';
import Draggable from './Draggable';
import Droppable from './Droppable';
import DeleteBucketModal from './DeleteBucketModal';
import ShareBucketModal from './ShareBucketModal';
import TodayModal from './TodayModal';
import FloatingCalendar from './FloatingCalendar';
import AnimatedLogo from './AnimatedLogo';
import '../bootstrap-icons.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://wsticket.frozentoasty.de/api';

const modalContentStyle = {
  backgroundColor: '#181a1b',
  color: 'white',
  padding: '20px',
  borderRadius: '8px',
  width: '600px', // increased width
  boxShadow: '0 4px 16px rgba(0,0,0,0.7)',
  fontFamily: 'Arial, sans-serif',
};

const formGroupStyle = {
  marginBottom: '15px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start', // align items to the left
};

const labelStyle = {
  display: 'block',
  minWidth: '120px', // fixed width for label
  marginRight: '10px',
  fontWeight: 'bold',
};

const inputStyle = {
  flex: 1,
  padding: '8px',
  border: '1px solid #444',
  borderRadius: '4px',
  fontSize: '14px',
  backgroundColor: '#23272a',
  color: 'white',
  marginRight: '0',
  // Style calendar icon for date input
  accentColor: '#6c757d', // fallback for some browsers
  // For Webkit browsers (Chrome, Safari)
  WebkitTextFillColor: 'white',
};

const selectStyle = {
  ...inputStyle,
  width: '55%', // wider dropdown
};

const textareaStyle = {
  ...inputStyle,
  height: '180px',
  resize: 'none',
  // Custom scrollbar for dark mode
  scrollbarColor: '#23272a #181a1b', // thumb color, track color
  scrollbarWidth: 'thin',
};

const buttonGroupStyle = {
  display: 'flex',
  justifyContent: 'space-between',
};

const buttonStyle = {
  padding: '10px 15px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px',
};

const primaryButtonStyle = {
  ...buttonStyle,
  backgroundColor: '#007BFF',
  color: 'white',
};

const secondaryButtonStyle = {
  ...buttonStyle,
  backgroundColor: '#6c757d',
  color: 'white',
};

const bucketStyle = {
  padding: '12px',
  backgroundColor: 'rgba(0, 0, 0, 0.6)', // Slightly more opaque for better contrast
  borderRadius: '6px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  width: '250px', // Reduced width for more compact view
  minWidth: '250px', // Ensure consistent width
  maxHeight: '80vh', // Limit height to prevent excessive scrolling
  overflowY: 'auto', // Add scroll for overflow content
  display: 'flex',
  flexDirection: 'column',
  gap: '6px', // Reduced gap between items
  flex: 'none', // Don't grow/shrink
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.1)', // Subtle border for definition
};

const addButtonStyle = {
  padding: '15px 30px',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '16px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent dark background
  color: 'white',
  margin: '20px auto',
  display: 'block',
  position: 'sticky',
  top: '80px', // Position below the navbar
  zIndex: 50,
};

const ticketStyle = {
  padding: '8px 10px', // Slightly more padding for better readability
  margin: '0 0 4px 0', // Reduced margin between tickets
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '4px',
  fontSize: '13px', // Slightly larger for better readability
  color: 'white',
  backgroundColor: 'rgba(255, 255, 255, 0.05)', // Subtle background
  transition: 'all 0.2s ease',
  cursor: 'pointer',
};

function getPriorityColor(priority) {
  switch (priority) {
    case 'High':
      return 'rgba(80, 0, 0, 0.65)'; // darker red tint for high priority
    case 'Medium':
    case 'Low':
    default:
      return 'rgba(0, 0, 0, 0.5)'; // default dark for low and medium
  }
}

// Define the insertSorted function to resolve the error
const insertSorted = (array, item, compareFn) => {
  let left = 0;
  let right = array.length;
  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    if (compareFn(item, array[mid]) < 0) {
      right = mid;
    } else {
      left = mid + 1;
    }
  }
  array.splice(left, 0, item);
};

// Define ticket comparison function for sorting
const compareTickets = (a, b) => {
  // First priority: priority (High > Medium > Low)
  const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1, '': 0 };
  const priorityA = priorityOrder[a.priority] || 0;
  const priorityB = priorityOrder[b.priority] || 0;
  const priorityDiff = priorityB - priorityA; // Higher priority first
  
  if (priorityDiff !== 0) return priorityDiff;
  
  // Second priority: age within same priority (oldest first)
  const dateA = new Date(a.createdAt);
  const dateB = new Date(b.createdAt);
  
  return dateA - dateB; // Older tickets first
};

// Function to insert task at correct sorted position
const insertTaskSorted = (taskIds, newTaskId, allTasks) => {
  const newTask = allTasks[newTaskId];
  if (!newTask) return [...taskIds, newTaskId]; // fallback
  
  // Get tasks for current taskIds
  const tasks = taskIds.map(id => allTasks[id]).filter(Boolean);
  
  // Find correct insertion position
  let insertIndex = 0;
  for (let i = 0; i < tasks.length; i++) {
    if (compareTickets(newTask, tasks[i]) < 0) {
      insertIndex = i;
      break;
    }
    insertIndex = i + 1;
  }
  
  // Insert at correct position
  const newTaskIds = [...taskIds];
  newTaskIds.splice(insertIndex, 0, newTaskId);
  return newTaskIds;
};

function TicketBoard({ refreshKey }) {
  const { currentUser } = useContext(AuthContext);
  const [data, setData] = useState({ tasks: {}, buckets: {}, bucketOrder: [] });
  const bucketContainerRef = useClickAndDragScroll();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedBucketId, setSelectedBucketId] = useState(null);
  const [showInput, setShowInput] = useState({});
  const [recentlyCreatedTickets, setRecentlyCreatedTickets] = useState(new Set());
  const [isDeleteBucketModalOpen, setIsDeleteBucketModalOpen] = useState(false);
  const [bucketToDelete, setBucketToDelete] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [bucketToShare, setBucketToShare] = useState(null);
  const [showDoneTickets, setShowDoneTickets] = useState(false);
  const [animatingTickets, setAnimatingTickets] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [activeTimers, setActiveTimers] = useState(new Map());
  const [isTodayModalOpen, setIsTodayModalOpen] = useState(false);
  const [hiddenBuckets, setHiddenBuckets] = useState(new Set());
  const [animatingBuckets, setAnimatingBuckets] = useState(new Set());
  const [initialLoad, setInitialLoad] = useState(true);

  const updateSelectedTicket = useCallback((ticket) => {
    selectedTicketRef.current = ticket;
    setSelectedTicket(ticket);
  }, []);

  const bucketRefs = useRef({});
  const bucketNavbarRef = useRef(null);
  const navHighlightTimeoutRef = useRef(null);
  const glowTimeoutRef = useRef(null);
  const selectedTicketRef = useRef(null);

  // Configure drag sensors for better performance
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum distance before drag starts
      },
    })
  );

  // Set up axios defaults with token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  // Load active timers on component mount
  useEffect(() => {
    const loadActiveTimers = async () => {
      try {
        const response = await axios.get(`${API_URL}/timetracking/active`);
        const timers = new Map();
        response.data.forEach(timer => {
          timers.set(timer.ticketId._id || timer.ticketId, timer._id);
        });
        setActiveTimers(timers);
      } catch (error) {
        console.error('Error loading active timers:', error);
      }
    };
    
    if (!isLoading) {
      loadActiveTimers();
    }
  }, [isLoading]);

  // Mobile detection effect
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const fetchBucketsAndTickets = async () => {
      try {
        // Fetch user preferences first (including background and brightness)
        let selectedBg = 'background9.png';
        let brightness = 100;
        try {
          const preferencesResponse = await axios.get(`${API_URL}/users/preferences`);
          const userHiddenBuckets = preferencesResponse.data.hiddenBuckets || [];
          selectedBg = preferencesResponse.data.selectedBackground || 'background9.png';
          brightness = preferencesResponse.data.backgroundBrightness !== undefined ? preferencesResponse.data.backgroundBrightness : 100;
          setHiddenBuckets(new Set(userHiddenBuckets));
          // Store background and brightness in localStorage as fallback
          localStorage.setItem('selectedBackground', selectedBg);
          localStorage.setItem('backgroundBrightness', brightness.toString());
        } catch (error) {
          console.error('Error loading user preferences:', error);
          setHiddenBuckets(new Set());
          // Fallback to localStorage if API fails
          selectedBg = localStorage.getItem('selectedBackground') || 'background9.png';
          brightness = parseInt(localStorage.getItem('backgroundBrightness') || '100');
        }

        // Fetch buckets
        const bucketsResponse = await axios.get(`${API_URL}/buckets`);
        const bucketsData = bucketsResponse.data;

        const buckets = {};
        const bucketOrder = [];

        // Sort buckets to ensure is_new buckets come first
        bucketsData.sort((a, b) => {
          if (a.is_new && !b.is_new) return -1; // a comes first
          if (!a.is_new && b.is_new) return 1;  // b comes first
          return 0; // maintain original order for same type
        });

        bucketsData.forEach((bucket) => {
          buckets[bucket._id] = {
            id: bucket._id,
            _id: bucket._id,
            title: bucket.title,
            taskIds: bucket.taskIds || [],
            is_new: bucket.is_new || false,
            owner: bucket.owner,
            sharedWith: bucket.sharedWith || [],
            brainSync: bucket.brainSync || false,
          };
          bucketOrder.push(bucket._id);
        });

        // Fetch tickets after buckets
        const ticketsResponse = await axios.get(`${API_URL}/tickets`);
        const tickets = ticketsResponse.data;
        // Sort tickets using the shared comparison function
        tickets.sort(compareTickets);

        const tasks = {};

        tickets.forEach((ticket) => {
          if (!ticket.bucket || !ticket.bucket._id) {
            console.warn('Skipping ticket with invalid or missing bucket:', ticket);
            return; // Skip tickets with invalid or missing bucket
          }

          tasks[ticket._id] = {
            id: ticket._id,
            content: ticket.title,
            ...ticket,
          };

          if (!buckets[ticket.bucket._id]) {
            console.warn(`Bucket ${ticket.bucket._id} not found for ticket. Skipping.`);
            return;
          }

          // Only add the ticket to the bucket if it's not already there
          if (!buckets[ticket.bucket._id].taskIds.includes(ticket._id)) {
            buckets[ticket.bucket._id].taskIds.push(ticket._id);
          }
        });

        // Clean up any duplicate taskIds in buckets
        Object.keys(buckets).forEach(bucketId => {
          buckets[bucketId].taskIds = [...new Set(buckets[bucketId].taskIds)];
        });

        setData({ tasks, buckets, bucketOrder });
        setIsLoading(false);

        // Apply background and brightness after data is loaded and DOM is updated
        setTimeout(() => {
          const container = document.querySelector('.bucket-container-scroll');
          if (container) {
            container.style.backgroundImage = `url(/background/${selectedBg})`;
            container.style.setProperty('--bg-brightness', brightness);
          }
          setInitialLoad(false);
        }, 100);
      } catch (error) {
        console.error('Error fetching buckets or tickets:', error);
        setIsLoading(false);
      }
    };

    fetchBucketsAndTickets();
  }, [refreshKey]);

  useEffect(() => {
    const handleTicketCreated = (ticket) => {
      if (!ticket || !ticket._id || !ticket.title || !ticket.bucket) {
        return;
      }

      setData((prevData) => {
        if (prevData.tasks[ticket._id]) {
          return prevData;
        }

        const newTask = {
          id: ticket._id,
          content: ticket.title,
          ...ticket,
        };

        const updatedTasks = {
          ...prevData.tasks,
          [ticket._id]: newTask,
        };

        const updatedBuckets = { ...prevData.buckets };
        const bucketId = typeof ticket.bucket === 'object' ? ticket.bucket._id : ticket.bucket;

        if (!updatedBuckets[bucketId]) {
          return prevData;
        }

        if (!updatedBuckets[bucketId].taskIds.includes(ticket._id)) {
          updatedBuckets[bucketId] = {
            ...updatedBuckets[bucketId],
            taskIds: insertTaskSorted(
              updatedBuckets[bucketId].taskIds,
              ticket._id,
              updatedTasks
            ),
          };
        }

        return {
          ...prevData,
          tasks: updatedTasks,
          buckets: updatedBuckets,
        };
      });
    };

    const handleTicketUpdated = (ticket) => {
      setData((prevData) => {
        if (!prevData.tasks[ticket._id]) {
          return prevData;
        }

        const updatedTasks = {
          ...prevData.tasks,
          [ticket._id]: {
            id: ticket._id,
            content: ticket.title,
            ...ticket,
          },
        };

        const bucketId = typeof ticket.bucket === 'object' ? ticket.bucket._id : ticket.bucket;
        const oldBucketId = prevData.tasks[ticket._id].bucket;
        const oldBucketIdResolved = typeof oldBucketId === 'object' ? oldBucketId._id : oldBucketId;

        const updatedBuckets = { ...prevData.buckets };

        if (bucketId !== oldBucketIdResolved) {
          if (updatedBuckets[oldBucketIdResolved]) {
            updatedBuckets[oldBucketIdResolved] = {
              ...updatedBuckets[oldBucketIdResolved],
              taskIds: updatedBuckets[oldBucketIdResolved].taskIds.filter(
                (id) => id !== ticket._id
              ),
            };
          }
          if (updatedBuckets[bucketId] && !updatedBuckets[bucketId].taskIds.includes(ticket._id)) {
            updatedBuckets[bucketId] = {
              ...updatedBuckets[bucketId],
              taskIds: insertTaskSorted(
                updatedBuckets[bucketId].taskIds,
                ticket._id,
                updatedTasks
              ),
            };
          }
        }

        return {
          ...prevData,
          tasks: updatedTasks,
          buckets: updatedBuckets,
        };
      });

      if (selectedTicketRef.current && selectedTicketRef.current._id === ticket._id) {
        setSelectedTicket(ticket);
        selectedTicketRef.current = ticket;
      }
    };

    const handleTicketFieldUpdate = ({ ticketId, field, value }) => {
      setData((prevData) => {
        if (!prevData.tasks[ticketId]) return prevData;
        const updatedTasks = {
          ...prevData.tasks,
          [ticketId]: {
            ...prevData.tasks[ticketId],
            [field]: value,
          },
        };
        return {
          ...prevData,
          tasks: updatedTasks,
        };
      });
    };

    const handleTicketDeleted = (deletedTicketId) => {
      setData((prevData) => {
        const updatedTasks = { ...prevData.tasks };
        delete updatedTasks[deletedTicketId];

        const updatedBuckets = { ...prevData.buckets };
        Object.keys(updatedBuckets).forEach((bucketId) => {
          updatedBuckets[bucketId] = {
            ...updatedBuckets[bucketId],
            taskIds: updatedBuckets[bucketId].taskIds.filter((id) => id !== deletedTicketId),
          };
        });

        return {
          ...prevData,
          tasks: updatedTasks,
          buckets: updatedBuckets,
        };
      });
    };

    const handleBucketShared = (data) => {
      const { bucket } = data;
      const isSharedWithMe = bucket.sharedWith.some(user => user.username === currentUser.username);

      if (isSharedWithMe) {
        setData(prevData => {
          const newBucket = {
            id: bucket._id,
            title: bucket.title,
            taskIds: [],
            is_new: bucket.is_new || false,
            owner: bucket.owner,
            sharedWith: bucket.sharedWith
          };

          const updatedBuckets = { ...prevData.buckets };
          updatedBuckets[bucket._id] = newBucket;

          const updatedBucketOrder = [...prevData.bucketOrder];
          if (!updatedBucketOrder.includes(bucket._id)) {
            if (bucket.is_new) {
              updatedBucketOrder.unshift(bucket._id);
            } else {
              updatedBucketOrder.push(bucket._id);
            }
          }

          return {
            ...prevData,
            buckets: updatedBuckets,
            bucketOrder: updatedBucketOrder
          };
        });
      }
    };

    const handleBucketUnshared = (data) => {
      const { bucket, unsharedUserId } = data;

      if (unsharedUserId === currentUser.id) {
        setData(prevData => {
          const updatedBuckets = { ...prevData.buckets };
          delete updatedBuckets[bucket._id];

          const updatedBucketOrder = prevData.bucketOrder.filter(id => id !== bucket._id);

          return {
            ...prevData,
            buckets: updatedBuckets,
            bucketOrder: updatedBucketOrder
          };
        });
      }
    };

    socketService.on('ticketCreated', handleTicketCreated);
    socketService.on('ticketUpdated', handleTicketUpdated);
    socketService.on('ticket-updated', handleTicketFieldUpdate);
    socketService.on('ticketDeleted', handleTicketDeleted);
    socketService.on('bucketShared', handleBucketShared);
    socketService.on('bucketUnshared', handleBucketUnshared);

    const unsubscribeReconnect = socketService.onReconnect(() => {
      const fetchBucketsAndTickets = async () => {
        try {
          const token = localStorage.getItem('token');
          if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          }

          const bucketsResponse = await axios.get(`${API_URL}/buckets`);
          const bucketsData = bucketsResponse.data;

          const buckets = {};
          const bucketOrder = [];

          bucketsData.sort((a, b) => {
            if (a.is_new && !b.is_new) return -1;
            if (!a.is_new && b.is_new) return 1;
            return 0;
          });

          bucketsData.forEach((bucket) => {
            buckets[bucket._id] = {
              id: bucket._id,
              _id: bucket._id,
              title: bucket.title,
              taskIds: bucket.taskIds || [],
              is_new: bucket.is_new || false,
              owner: bucket.owner,
              sharedWith: bucket.sharedWith || [],
              brainSync: bucket.brainSync || false,
            };
            bucketOrder.push(bucket._id);
          });

          const ticketsResponse = await axios.get(`${API_URL}/tickets`);
          const tickets = ticketsResponse.data;
          tickets.sort(compareTickets);

          const tasks = {};

          tickets.forEach((ticket) => {
            if (!ticket.bucket || !ticket.bucket._id) return;

            tasks[ticket._id] = {
              id: ticket._id,
              content: ticket.title,
              ...ticket,
            };

            if (!buckets[ticket.bucket._id]) return;

            if (!buckets[ticket.bucket._id].taskIds.includes(ticket._id)) {
              buckets[ticket.bucket._id].taskIds.push(ticket._id);
            }
          });

          Object.keys(buckets).forEach(bucketId => {
            buckets[bucketId].taskIds = [...new Set(buckets[bucketId].taskIds)];
          });

          setData({ tasks, buckets, bucketOrder });
        } catch (error) {
          console.error('Error refetching on reconnect:', error);
        }
      };

      fetchBucketsAndTickets();
    });

    return () => {
      socketService.off('ticketCreated', handleTicketCreated);
      socketService.off('ticketUpdated', handleTicketUpdated);
      socketService.off('ticket-updated', handleTicketFieldUpdate);
      socketService.off('ticketDeleted', handleTicketDeleted);
      socketService.off('bucketShared', handleBucketShared);
      socketService.off('bucketUnshared', handleBucketUnshared);
      unsubscribeReconnect();
    };
  }, [refreshKey, currentUser]);



  // Function to scroll to a specific bucket
  const scrollToBucket = useCallback((bucketId) => {
    const bucketElement = bucketRefs.current[bucketId];
    
    if (!bucketElement) {
      console.warn('Bucket element not found for ID:', bucketId);
      return;
    }
    
    try {
      // Get the container that holds all buckets
      const container = bucketElement.parentElement;
      if (container) {
        if (isMobile) {
          // Mobile: scroll to center the bucket in viewport
          const elementLeft = bucketElement.offsetLeft;
          const elementWidth = bucketElement.offsetWidth;
          const containerWidth = container.clientWidth;
          const scrollLeft = elementLeft - (containerWidth / 2) + (elementWidth / 2);
          container.scrollTo({
            left: scrollLeft,
            behavior: 'smooth'
          });
        } else {
          // Desktop: center the bucket in the view
          const elementLeft = bucketElement.offsetLeft;
          const elementWidth = bucketElement.offsetWidth;
          const containerWidth = container.clientWidth;
          const scrollLeft = elementLeft - (containerWidth / 2) + (elementWidth / 2);
          
          container.scrollTo({
            left: scrollLeft,
            behavior: 'smooth'
          });
        }
      }
      
      // Clear any existing timeouts
      if (navHighlightTimeoutRef.current) {
        clearTimeout(navHighlightTimeoutRef.current);
      }
      if (glowTimeoutRef.current) {
        clearTimeout(glowTimeoutRef.current);
      }
      
      // Create a glow overlay element that's independent of React rendering
      const glowOverlay = document.createElement('div');
      glowOverlay.className = 'bucket-glow-overlay';
      glowOverlay.style.cssText = `
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        background: transparent;
        border-radius: 8px;
        pointer-events: none;
        z-index: 10;
        box-shadow: 0 0 15px rgba(74, 158, 255, 0.6), 0 0 30px rgba(74, 158, 255, 0.3);
        opacity: 1;
        transition: opacity 3s ease-out;
      `;
      
      // Ensure the bucket element has relative positioning
      bucketElement.style.position = 'relative';
      
      // Add the overlay to the bucket
      bucketElement.appendChild(glowOverlay);
      
      // Trigger the fade-out animation
      requestAnimationFrame(() => {
        glowOverlay.style.opacity = '0';
      });
      
      // Remove the overlay after animation completes
      glowTimeoutRef.current = setTimeout(() => {
        if (glowOverlay.parentNode) {
          glowOverlay.parentNode.removeChild(glowOverlay);
        }
      }, 3000);
      
      // Apply navbar highlight directly to DOM element to avoid rerender
      if (bucketNavbarRef.current) {
        // Clear any existing highlight from previous bucket
        if (navHighlightTimeoutRef.current) {
          clearTimeout(navHighlightTimeoutRef.current);
          // Clear all highlights from all buttons
          bucketNavbarRef.current.clearAllHighlights();
        }
        
        // Apply new highlight
        bucketNavbarRef.current.highlightButton(bucketId);
        
        // Set timeout to remove navbar highlight after 2 seconds
        navHighlightTimeoutRef.current = setTimeout(() => {
          if (bucketNavbarRef.current) {
            bucketNavbarRef.current.clearHighlight(bucketId);
          }
        }, 2000);
      }
      
    } catch (error) {
      console.error('Scrolling error:', error);
    }
  }, [isMobile]);

  // Memoized callback for bucket title change
  const handleBucketTitleChange = useCallback((bucketId, newTitle) => {
    if (bucketId === 'bucket-1') {
      console.warn('Bucket "Neu" cannot be renamed.');
      return;
    }

    setData((prevData) => ({
      ...prevData,
      buckets: {
        ...prevData.buckets,
        [bucketId]: {
          ...prevData.buckets[bucketId],
          title: newTitle,
        },
      },
    }));
  }, []);

  // Memoized callback for bucket title save
  const handleBucketTitleSave = useCallback(async (bucketId, title) => {
    try {
      await axios.put(`${API_URL}/buckets/${bucketId}`, {
        title: title,
      });
      setData((prevData) => ({
        ...prevData,
        buckets: {
          ...prevData.buckets,
          [bucketId]: {
            ...prevData.buckets[bucketId],
            isEditingTitle: false,
          },
        },
      }));
    } catch (error) {
      console.error('Error saving bucket title:', error);
    }
  }, []);

  // Memoized callback for starting bucket title edit
  const handleBucketTitleEdit = useCallback((bucketId) => {
    setData((prevData) => ({
      ...prevData,
      buckets: {
        ...prevData.buckets,
        [bucketId]: {
          ...prevData.buckets[bucketId],
          isEditingTitle: true,
        },
      },
    }));
  }, []);

  // Handle ticket status change
  const handleTicketStatusChange = useCallback(async (ticketId, newStatus) => {
    try {
      // If marking as done and there's an active timer, track this as completed step
      if (newStatus === 'done' && activeTimers.has(ticketId)) {
        const ticket = Object.values(data.tasks).find(t => t.id === ticketId);
        if (ticket) {
          try {
            await axios.post(`${API_URL}/timetracking/add-completed-step`, {
              ticketId: ticketId,
              stepText: `Ticket "${ticket.title}" als erledigt markiert`
            });
          } catch (error) {
            console.error('Error tracking completed step:', error);
          }
        }
      }
      
      // If marking as done and not showing done tickets, animate out
      if (newStatus === 'done' && !showDoneTickets) {
        setAnimatingTickets(prev => new Set(prev).add(ticketId));
        // Wait for animation to complete before updating
        setTimeout(() => {
          setAnimatingTickets(prev => {
            const newSet = new Set(prev);
            newSet.delete(ticketId);
            return newSet;
          });
        }, 200);
      }
      
      await axios.put(`${API_URL}/tickets/${ticketId}`, { status: newStatus });
      // The WebSocket will handle the real-time update
    } catch (error) {
      console.error('Error updating ticket status:', error);
    }
  }, [showDoneTickets, activeTimers, data.tasks]);

  // Handle ticket deletion with animation
  const handleDeleteTicket = useCallback(async (ticketId) => {
    try {
      setAnimatingTickets(prev => new Set(prev).add(ticketId));
      // Wait for animation to complete before deleting
      setTimeout(async () => {
        try {
          await axios.delete(`${API_URL}/tickets/${ticketId}`);
          setAnimatingTickets(prev => {
            const newSet = new Set(prev);
            newSet.delete(ticketId);
            return newSet;
          });
          // The WebSocket will handle the real-time update
        } catch (error) {
          console.error('Error deleting ticket:', error);
          setAnimatingTickets(prev => {
            const newSet = new Set(prev);
            newSet.delete(ticketId);
            return newSet;
          });
        }
      }, 300);
    } catch (error) {
      console.error('Error starting delete animation:', error);
    }
  }, []);

  // Handle show done tickets toggle with animation
  const handleToggleDoneTickets = useCallback(() => {
    if (showDoneTickets) {
      // Hiding done tickets - animate them out first
      const doneTickets = Object.values(data.tasks).filter(task => task.status === 'done');
      const doneTicketIds = doneTickets.map(task => task.id);
      
      setAnimatingTickets(new Set(doneTicketIds));
      
      setTimeout(() => {
        setShowDoneTickets(false);
        setAnimatingTickets(new Set());
      }, 200);
    } else {
      // Showing done tickets - just toggle immediately (they'll animate in)
      setShowDoneTickets(true);
    }
  }, [showDoneTickets, data.tasks]);

  // Handle timer start
  const handleStartTimer = useCallback(async (ticketId, stepIndex = null, stepText = null, allowMultiple = false) => {
    try {
      // If not allowing multiple timers and there are active timers, stop them first
      if (!allowMultiple && activeTimers.size > 0) {
        // Stop all currently active timers
        const stopPromises = Array.from(activeTimers.entries()).map(async ([activeTicketId, timeEntryId]) => {
          try {
            await axios.post(`${API_URL}/timetracking/stop/${timeEntryId}`);
            return activeTicketId;
          } catch (error) {
            console.error('Error stopping timer for ticket:', activeTicketId, error);
            return null;
          }
        });
        
        const stoppedTickets = await Promise.all(stopPromises);
        
        // Clear stopped timers from state
        setActiveTimers(prev => {
          const newMap = new Map(prev);
          stoppedTickets.forEach(ticketId => {
            if (ticketId) newMap.delete(ticketId);
          });
          return newMap;
        });
      }
      
      const response = await axios.post(`${API_URL}/timetracking/start`, {
        ticketId: ticketId,
        stepIndex: stepIndex,
        stepText: stepText
      });
      
      setActiveTimers(prev => {
        const newMap = new Map(prev);
        newMap.set(ticketId, response.data._id);
        return newMap;
      });
    } catch (error) {
      console.error('Error starting timer:', error);
      alert('Fehler beim Starten des Timers: ' + (error.response && error.response.data && error.response.data.error ? error.response.data.error : error.message));
    }
  }, [activeTimers]);

  // Handle timer stop
  const handleStopTimer = useCallback(async (ticketId) => {
    try {
      const timeEntryId = activeTimers.get(ticketId);
      if (!timeEntryId) {
        console.error('No active timer found for ticket:', ticketId);
        return;
      }
      
      await axios.post(`${API_URL}/timetracking/stop/${timeEntryId}`);
      
      setActiveTimers(prev => {
        const newMap = new Map(prev);
        newMap.delete(ticketId);
        return newMap;
      });
    } catch (error) {
      console.error('Error stopping timer:', error);
      alert('Fehler beim Stoppen des Timers: ' + (error.response && error.response.data && error.response.data.error ? error.response.data.error : error.message));
    }
  }, [activeTimers]);

  // Update server when a ticket is moved (dragged)
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sourceBucket = Object.values(data.buckets).find((bucket) => bucket.taskIds.includes(active.id));
    const destinationBucket = Object.values(data.buckets).find((bucket) => bucket.id === over.id);

    if (!sourceBucket || !destinationBucket || sourceBucket.id === destinationBucket.id) {
      return;
    }

    // Store original state for rollback
    const originalData = { ...data };
    
    // Optimistic update - update UI immediately
    setData((prevData) => {
      const newData = { ...prevData };
      const newSourceBucket = { ...newData.buckets[sourceBucket.id] };
      const newDestinationBucket = { ...newData.buckets[destinationBucket.id] };
      
      // Remove from source
      newSourceBucket.taskIds = newSourceBucket.taskIds.filter(id => id !== active.id);
      
      // Add to destination at correct sorted position
      newDestinationBucket.taskIds = insertTaskSorted(
        newDestinationBucket.taskIds.filter(id => id !== active.id), // Remove if already exists
        active.id,
        newData.tasks
      );
      
      return {
        ...newData,
        buckets: {
          ...newData.buckets,
          [sourceBucket.id]: newSourceBucket,
          [destinationBucket.id]: newDestinationBucket,
        },
      };
    });

    try {
      // Update server
      await axios.put(`${API_URL}/tickets/${active.id}`, {
        ...data.tasks[active.id],
        bucket: destinationBucket.id,
      });
      // Success - optimistic update already applied
    } catch (error) {
      console.error('Error moving ticket:', error);
      // Rollback on error
      setData(originalData);
    }
  }, [data]);

  const handleAddTicket = async (ticket, ticketId) => {
    if (ticketId) {
      try {
        await axios.put(`${API_URL}/tickets/${ticketId}`, ticket);
      } catch (error) {
        console.error('Error updating ticket:', error);
      }
    } else {
      try {
        const neuBucket = Object.values(data.buckets).find(bucket => bucket.is_new);
        const bucketId = neuBucket ? neuBucket.id : Object.keys(data.buckets)[0];

        const ticketWithBucket = {
          ...ticket,
          bucket: bucketId,
        };

        const response = await axios.post(`${API_URL}/tickets`, ticketWithBucket);
        const createdTicket = response.data;

        const newTask = {
          id: createdTicket._id,
          content: createdTicket.title,
          ...createdTicket,
        };

        setData((prevData) => {
          if (prevData.tasks[createdTicket._id]) return prevData;

          const updatedTasks = {
            ...prevData.tasks,
            [createdTicket._id]: newTask,
          };

          const resolvedBucketId = typeof createdTicket.bucket === 'object' ? createdTicket.bucket._id : createdTicket.bucket;
          const updatedBuckets = { ...prevData.buckets };

          if (updatedBuckets[resolvedBucketId] && !updatedBuckets[resolvedBucketId].taskIds.includes(createdTicket._id)) {
            updatedBuckets[resolvedBucketId] = {
              ...updatedBuckets[resolvedBucketId],
              taskIds: insertTaskSorted(
                updatedBuckets[resolvedBucketId].taskIds,
                createdTicket._id,
                updatedTasks
              ),
            };
          }

          return {
            ...prevData,
            tasks: updatedTasks,
            buckets: updatedBuckets,
          };
        });
      } catch (error) {
        console.error('Error adding ticket:', error);
      }
    }

    updateSelectedTicket(null);
  };

  const handleTicketDoubleClick = (ticketId) => {
    const ticket = Object.values(data.tasks).find((task) => task.id === ticketId);
    if (ticket) {
      updateSelectedTicket(ticket);
      setIsModalOpen(true);
      axios.get(`${API_URL}/tickets/${ticketId}`).then((response) => {
        const t = response.data;
        const fresh = { id: t._id, content: t.title, ...t };
        setData((prev) => ({
          ...prev,
          tasks: { ...prev.tasks, [ticketId]: fresh },
        }));
      }).catch(() => {});
    }
  };

  const handleTicketClick = (ticketId) => {
  };

  const handleTicketRightClick = (event, ticketId) => {
    event.preventDefault();
    const ticket = Object.values(data.tasks).find((task) => task.id === ticketId);
    if (ticket) {
      updateSelectedTicket(ticket);
      setIsDetailsModalOpen(true);
    }
  };

  const handleTicketAltClick = (event, ticketId) => {
    if (event.altKey) {
      const ticket = Object.values(data.tasks).find((task) => task.id === ticketId);
      if (ticket) {
        setSelectedTicket(ticket);
        setIsModalOpen(true);
        axios.get(`${API_URL}/tickets/${ticketId}`).then((response) => {
          const t = response.data;
          const fresh = { id: t._id, content: t.title, ...t };
          setData((prev) => ({
            ...prev,
            tasks: { ...prev.tasks, [ticketId]: fresh },
          }));
        }).catch(() => {});
      }
    }
  }


  const handleAddBucket = async () => {
    try {
      // Let the backend determine the appropriate title (Neu for first bucket, or prompt user for others)
      const title = data.bucketOrder.length === 0 ? undefined : prompt('Enter bucket title:') || 'New Bucket';
      const response = await axios.post(`${API_URL}/buckets`, { title });
      const newBucket = response.data;

      setData((prevData) => ({
        ...prevData,
        buckets: {
          ...prevData.buckets,
          [newBucket._id]: {
            id: newBucket._id,
            _id: newBucket._id,
            title: newBucket.title,
            taskIds: [],
            is_new: newBucket.is_new || false,
            owner: newBucket.owner,
            sharedWith: newBucket.sharedWith || [],
            brainSync: newBucket.brainSync || false,
          },
        },
        bucketOrder: newBucket.is_new 
          ? [newBucket._id, ...prevData.bucketOrder] // Add is_new buckets to the beginning
          : [...prevData.bucketOrder, newBucket._id], // Add regular buckets to the end
      }));
    } catch (error) {
      console.error('Error creating bucket:', error);
    }
  };

  const handleQuickAddTicket = async (bucketId, ticketTitle) => {
    try {
      const ticketData = {
        title: ticketTitle,
        bucket: bucketId,
        description: '',
        dueDate: null,
        project: '',
        priority: 'Medium',
        steps: [],
      };

      const response = await axios.post(`${API_URL}/tickets`, ticketData);
      const createdTicket = response.data;

      const newTask = {
        id: createdTicket._id,
        content: createdTicket.title,
        ...createdTicket,
      };

      setData((prevData) => {
        if (prevData.tasks[createdTicket._id]) return prevData;

        const updatedTasks = {
          ...prevData.tasks,
          [createdTicket._id]: newTask,
        };

        const resolvedBucketId = typeof createdTicket.bucket === 'object' ? createdTicket.bucket._id : createdTicket.bucket;
        const updatedBuckets = { ...prevData.buckets };

        if (updatedBuckets[resolvedBucketId] && !updatedBuckets[resolvedBucketId].taskIds.includes(createdTicket._id)) {
          updatedBuckets[resolvedBucketId] = {
            ...updatedBuckets[resolvedBucketId],
            taskIds: insertTaskSorted(
              updatedBuckets[resolvedBucketId].taskIds,
              createdTicket._id,
              updatedTasks
            ),
          };
        }

        return {
          ...prevData,
          tasks: updatedTasks,
          buckets: updatedBuckets,
        };
      });
    } catch (error) {
      console.error('Error creating ticket:', error);
    }
  };

  const handleDeleteBucketClick = (bucketId) => {
    setBucketToDelete(bucketId);
    setIsDeleteBucketModalOpen(true);
  };

  const confirmDeleteBucket = async () => {
    if (!bucketToDelete) return;

    try {
      const response = await axios.delete(`${API_URL}/buckets/${bucketToDelete}`);
      if (response.status === 200) {
        setData((prevData) => {
          const updatedBuckets = { ...prevData.buckets };
          delete updatedBuckets[bucketToDelete];

          const updatedBucketOrder = prevData.bucketOrder.filter((id) => id !== bucketToDelete);

          return {
            ...prevData,
            buckets: updatedBuckets,
            bucketOrder: updatedBucketOrder,
          };
        });
      }
    } catch (error) {
      console.error('Error deleting bucket:', error);
      if (error.response && error.response.status === 409) {
        alert('Cannot delete bucket that contains tickets. Please move or delete all tickets first.');
      } else {
        alert('Error deleting bucket. Please try again.');
      }
    } finally {
      setIsDeleteBucketModalOpen(false);
      setBucketToDelete(null);
    }
  };

  const handleShareBucketClick = (bucketId) => {
    const bucketData = data.buckets[bucketId];
    if (bucketData) {
      // Ensure the bucket object has the _id field
      const bucketWithId = {
        ...bucketData,
        _id: bucketData.id || bucketData._id
      };
      setBucketToShare(bucketWithId);
      setIsShareModalOpen(true);
    } else {
      console.error('Bucket not found:', bucketId);
    }
  };

  const handleBucketShared = (updatedBucket) => {
    setData(prevData => ({
      ...prevData,
      buckets: {
        ...prevData.buckets,
        [updatedBucket._id]: {
          ...prevData.buckets[updatedBucket._id],
          sharedWith: updatedBucket.sharedWith,
          owner: updatedBucket.owner
        }
      }
    }));
  };

  const handleBrainSyncToggle = useCallback(async (bucketId) => {
    const bucketData = data.buckets[bucketId];
    if (!bucketData) return;

    const newSync = !bucketData.brainSync;

    setData(prevData => ({
      ...prevData,
      buckets: {
        ...prevData.buckets,
        [bucketId]: {
          ...prevData.buckets[bucketId],
          brainSync: newSync,
        }
      }
    }));

    try {
      await axios.put(`${API_URL}/buckets/${bucketId}/brain-sync`, { brainSync: newSync }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (err) {
      console.error('Error toggling brain sync:', err);
      setData(prevData => ({
        ...prevData,
        buckets: {
          ...prevData.buckets,
          [bucketId]: {
            ...prevData.buckets[bucketId],
            brainSync: !newSync,
          }
        }
      }));
    }
  }, [data.buckets]);

  const handleShowToday = () => {
    setIsTodayModalOpen(true);
  };

  const handleAddStep = useCallback(async (stepText) => {
    if (!selectedTicket || !stepText.trim()) return;

    try {
      // Erstelle neuen Step mit Timestamp-Struktur
      const updatedSteps = [...selectedTicket.steps, stepText.trim()];
      await axios.put(`${API_URL}/tickets/${selectedTicket._id}`, {
        ...selectedTicket,
        steps: updatedSteps
      });

      // Falls ein Timer für dieses Ticket läuft, füge den Step auch dort hinzu
      if (activeTimers.has(selectedTicket._id)) {
        try {
          await axios.post(`${API_URL}/timetracking/add-completed-step`, {
            ticketId: selectedTicket._id,
            stepText: `Step hinzugefügt: "${stepText.trim()}"`
          });
        } catch (error) {
          console.error('Error tracking step addition:', error);
        }
      }

      // The WebSocket will handle the real-time update
    } catch (error) {
      console.error('Error adding step:', error);
      alert('Fehler beim Hinzufügen des Steps: ' + (error.response && error.response.data && error.response.data.error ? error.response.data.error : error.message));
    }
  }, [selectedTicket, activeTimers]);


  // Handle bucket visibility toggle
  const handleToggleBucketVisibility = useCallback(async (bucketId) => {
    setHiddenBuckets(prev => {
      const isCurrentlyHidden = prev.has(bucketId);
      const newSet = new Set(prev);

      if (isCurrentlyHidden) {
        // Showing bucket - remove from hidden immediately (fade-in will happen)
        newSet.delete(bucketId);

        // Update server immediately for showing
        const updateServer = async () => {
          try {
            await axios.put(`${API_URL}/users/preferences/hidden-buckets`, {
              hiddenBuckets: Array.from(newSet)
            });
          } catch (error) {
            console.error('Error updating hidden buckets on server:', error);
          }
        };
        updateServer();
      } else {
        // Hiding bucket - start animation first
        setAnimatingBuckets(animPrev => new Set(animPrev).add(bucketId));

        // Hide bucket after animation completes
        setTimeout(async () => {
          setHiddenBuckets(hiddenPrev => {
            const hiddenSet = new Set(hiddenPrev).add(bucketId);

            // Update server after hiding
            const updateServer = async () => {
              try {
                await axios.put(`${API_URL}/users/preferences/hidden-buckets`, {
                  hiddenBuckets: Array.from(hiddenSet)
                });
              } catch (error) {
                console.error('Error updating hidden buckets on server:', error);
              }
            };
            updateServer();

            return hiddenSet;
          });
          setAnimatingBuckets(animPrev => {
            const newAnimSet = new Set(animPrev);
            newAnimSet.delete(bucketId);
            return newAnimSet;
          });
        }, 400); // Match animation duration
      }

      return newSet;
    });
  }, []);

  // Loading component - animiertes WST-Logo
  const LoadingSpinner = () => (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 24,
      backgroundColor: 'rgba(0, 0, 0, 0.92)',
      zIndex: 9999
    }}>
      <AnimatedLogo animated variant="builder" width={260} />
      <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 16, fontWeight: 300, margin: 0 }}>
        Loading WS Tickets...
      </p>
    </div>
  );

  // While loading, return null - the HTML spinner from index.html stays visible
  if (isLoading) {
    return null;
  }

  // Hide the initial HTML loader when TicketBoard is ready
  const hideInitialLoader = () => {
    const loader = document.getElementById('initial-loader');
    if (loader && !loader.classList.contains('hidden')) {
      loader.classList.add('hidden');
    }
  };
  hideInitialLoader();


  return (
    <div style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.5s ease-in-out' }}>
      <Navbar
        onAddBucket={handleAddBucket}
        showDoneTickets={showDoneTickets}
        onToggleDoneTickets={handleToggleDoneTickets}
        onShowToday={handleShowToday}
        isTimerActive={activeTimers.size > 0}
      />

      
      <BucketNavbar
        ref={bucketNavbarRef}
        bucketOrder={data.bucketOrder}
        buckets={data.buckets}
        onBucketClick={scrollToBucket}
        isTimerActive={activeTimers.size > 0}
        hiddenBuckets={hiddenBuckets}
        onToggleBucketVisibility={handleToggleBucketVisibility}
        onReorder={(newOrder) => setData((prev) => ({ ...prev, bucketOrder: newOrder }))}
      />
      
      <AddTicketModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); updateSelectedTicket(null); }}
        onAddTicket={handleAddTicket}
        onDelete={(ticketId) => {
          setData((prevData) => {
            const updatedTasks = { ...prevData.tasks };
            delete updatedTasks[ticketId];
            const updatedBuckets = { ...prevData.buckets };
            Object.keys(updatedBuckets).forEach((bId) => {
              updatedBuckets[bId] = {
                ...updatedBuckets[bId],
                taskIds: updatedBuckets[bId].taskIds.filter((id) => id !== ticketId),
              };
            });
            return { ...prevData, tasks: updatedTasks, buckets: updatedBuckets };
          });
        }}
        ticket={selectedTicket}
      />
      <TicketDetailsModal
        isOpen={isDetailsModalOpen}
        ticket={selectedTicket}
        onClose={() => { setIsDetailsModalOpen(false); updateSelectedTicket(null); }}
        activeTimers={activeTimers}
        onStartTimer={handleStartTimer}
        onStopTimer={handleStopTimer}
      />
      <DeleteBucketModal
        isOpen={isDeleteBucketModalOpen}
        onClose={() => setIsDeleteBucketModalOpen(false)}
        onConfirm={confirmDeleteBucket}
      />
      <ShareBucketModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        bucket={bucketToShare}
        onShare={handleBucketShared}
      />
      <TodayModal
        isOpen={isTodayModalOpen}
        onClose={() => setIsTodayModalOpen(false)}
        buckets={data.buckets}
      />

      <FloatingCalendar
        tasks={data.tasks}
        onTicketClick={(ticket) => {
          updateSelectedTicket(ticket);
          setIsDetailsModalOpen(true);
        }}
      />
      
      <DndContext 
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div 
          ref={bucketContainerRef}
          className="bucket-container-scroll"
          style={{ 
            display: 'flex', 
            gap: '7px', 
            padding: '15px',
            overflowX: 'auto',
            overflowY: 'hidden',
            minHeight: 'calc(100vh - 120px)', // Account for both navbars (70px + 50px)
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent'
          }}>
          {data.bucketOrder.map((bucketId, index) => {
            if (hiddenBuckets.has(bucketId) && !animatingBuckets.has(bucketId)) {
              return null;
            }
            const bucket = data.buckets[bucketId];
            const allTasks = bucket.taskIds.map((taskId) => data.tasks[taskId]).filter(Boolean);
            const tasks = showDoneTickets ? allTasks : allTasks.filter(task =>
              task.status !== 'done' || animatingTickets.has(task.id)
            );
            const isAnimatingOut = animatingBuckets.has(bucketId);
            const isInitialBucket = initialLoad && !hiddenBuckets.has(bucketId);
            const isNewlyVisible = !initialLoad && !hiddenBuckets.has(bucketId) && !animatingBuckets.has(bucketId);

            return (
              <div
                key={`bucket-container-${bucketId}`}
                ref={el => bucketRefs.current[bucketId] = el}
                className={`bucket-container ${isAnimatingOut ? 'bucket-container--animating-out' : ''} ${isInitialBucket || isNewlyVisible ? 'bucket-container--fade-in' : ''}`}
                style={{ animationDelay: (isInitialBucket || isNewlyVisible) ? `${index * 0.1}s` : '0s' }}
              >
                <Droppable key={`bucket-${bucket.id}`} id={bucket.id}>
                {bucketId === 'bucket-1' ? (
                  <div className="bucket-title-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <h3 style={{ margin: '0', fontSize: '20px', fontWeight: '700' }}>{bucket.title}</h3>
                    </div>
                    <button
                      onClick={() => handleBrainSyncToggle(bucketId)}
                      style={{
                        marginLeft: '10px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: bucket.brainSync ? '#66bb6a' : '#666',
                        textDecoration: bucket.brainSync ? 'none' : 'line-through',
                        opacity: bucket.brainSync ? 1 : 0.5,
                        transition: 'all 0.2s',
                      }}
                      title={bucket.brainSync ? 'Vector Sync aktiv (klicken zum Deaktivieren)' : 'Vector Sync inaktiv (klicken zum Aktivieren)'}
                    >
                      <i className="bi bi-cpu"></i>
                    </button>
                  </div>
                ) : bucket.is_new ? (
                  <div className="bucket-title-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <h3 style={{ margin: '0', fontSize: '20px', fontWeight: '700' }}>{bucket.title}</h3>
                    </div>
                    <button
                      onClick={() => handleBrainSyncToggle(bucketId)}
                      style={{
                        marginLeft: '10px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: bucket.brainSync ? '#66bb6a' : '#666',
                        textDecoration: bucket.brainSync ? 'none' : 'line-through',
                        opacity: bucket.brainSync ? 1 : 0.5,
                        transition: 'all 0.2s',
                      }}
                      title={bucket.brainSync ? 'Vector Sync aktiv (klicken zum Deaktivieren)' : 'Vector Sync inaktiv (klicken zum Aktivieren)'}
                    >
                      <i className="bi bi-cpu"></i>
                    </button>
                  </div>
                ) : (
                  <div className="bucket-title-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {bucket.isEditingTitle ? (
                      <input
                        type="text"
                        value={bucket.title}
                        onChange={(e) => handleBucketTitleChange(bucketId, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleBucketTitleSave(bucket.id, bucket.title);
                          }
                        }}
                        style={{
                          fontSize: '1.5em',
                          fontWeight: 'bold',
                          border: 'none',
                          backgroundColor: 'transparent',
                          color: 'white',
                          width: '100%',
                          textAlign: 'center',
                        }}
                        autoFocus
                      />
                    ) : (
                      <div style={{ textAlign: 'center', margin: '0 0 12px 0' }}>
                        <h3 style={{ margin: '0', fontSize: '20px', fontWeight: '700' }}>{bucket.title}</h3>
                      </div>
                    )}
                    {!bucket.is_new && (
                      <>
                        <button
                          onClick={() => handleBrainSyncToggle(bucketId)}
                          style={{
                            marginLeft: '10px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: bucket.brainSync ? '#66bb6a' : '#666',
                            textDecoration: bucket.brainSync ? 'none' : 'line-through',
                            opacity: bucket.brainSync ? 1 : 0.5,
                          }}
                          title={bucket.brainSync ? 'Vector Sync aktiv (klicken zum Deaktivieren)' : 'Vector Sync inaktiv (klicken zum Aktivieren)'}
                        >
                          <i className="bi bi-cpu"></i>
                        </button>
                        <button
                          onClick={() => handleBucketTitleEdit(bucketId)}
                          style={{
                            marginLeft: '10px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: '16px',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Bearbeiten"
                        >
                          <i className="bi bi-pencil-square"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteBucketClick(bucketId)}
                          style={{
                            marginLeft: '10px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#dc3545',
                            fontSize: '16px',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Löschen"
                        >
                          <i className="bi bi-trash3"></i>
                        </button>
                        <button
                          onClick={() => handleShareBucketClick(bucketId)}
                          style={{
                            marginLeft: '10px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#4a9eff',
                            fontSize: '16px',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Teilen"
                        >
                          <i className="bi bi-share"></i>
                        </button>
                      </>
                    )}
                  </div>
                )}
                <div className="bucket-content">
                {tasks.map((task, taskIndex) => (
                  <div 
                    key={`ticket-${task.id}`} 
                    className={`ticket-item ${
                      animatingTickets.has(task.id) 
                        ? (task.status === 'done' ? 'ticket-item-done' : 'ticket-item-exit')
                        : ''
                    }`} 
                    style={{ 
                      animationDelay: animatingTickets.has(task.id) ? '0s' : `${Math.min(index * 0.03 + taskIndex * 0.01, 0.3)}s` 
                    }}
                  >
                    <Draggable
                      key={`bucket-${bucket.id}-task-${task.id}`}
                      id={task.id}
                      ticket={task}
                      onDoubleClick={handleTicketDoubleClick}
                      onStatusChange={handleTicketStatusChange}
                      onRightClick={handleTicketRightClick}
                      activeTimers={activeTimers}
                      onStartTimer={handleStartTimer}
                      onStopTimer={handleStopTimer}
                    >
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <p style={{ margin: '0 0 2px 0', fontSize: '15px', fontWeight: '600', lineHeight: '1.3', textAlign: 'left' }}>{task.title}</p>
                          {task.isGenerating && (
                            <span style={{ backgroundColor: '#F39C12', color: 'white', padding: '1px 5px', borderRadius: '3px', fontSize: '9px', fontWeight: 'bold', whiteSpace: 'nowrap', animation: 'pulse 1.5s infinite' }}>
                              KI generiert...
                            </span>
                          )}
                          {task.isZohoTicket && (
                            <span style={{ backgroundColor: '#9B59B6', color: 'white', padding: '1px 5px', borderRadius: '3px', fontSize: '9px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                              Zoho Desk
                            </span>
                          )}
                        </div>
                        {(task.projectRef?.name || task.project) && <p style={{ margin: '0', fontWeight: 'normal', fontSize: '13px', color: '#bbb', lineHeight: '1.3', textAlign: 'left' }}>📁 {task.projectRef?.name || task.project}</p>}
                      </div>
                    </Draggable>
                  </div>
                ))}
                <button
                  style={{
                    marginTop: '8px',
                    padding: '6px 8px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    backgroundColor: 'rgba(40, 167, 69, 0.8)',
                    color: 'white',
                    width: '100%',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = 'rgba(40, 167, 69, 1)';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'rgba(40, 167, 69, 0.8)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                  onClick={() => {
                    const ticketTitle = prompt('Enter ticket title:');
                    if (ticketTitle) {
                      handleQuickAddTicket(bucket.id, ticketTitle);
                    }
                  }}
                >
                  + Ticket
                </button>
                {showInput[bucket.id] && (
                  <input
                    type="text"
                    placeholder="Enter ticket title"
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const newTicketTitle = e.target.value;
                        if (newTicketTitle) {
                          try {
                            const response = await axios.post(`${API_URL}/tickets`, {
                              title: newTicketTitle,
                              bucket: bucket.id,
                            });
                            const created = response.data;
                            const newTask = { id: created._id, content: created.title, ...created };
                            setData((prev) => {
                              if (prev.tasks[created._id]) return prev;
                              const updatedTasks = { ...prev.tasks, [created._id]: newTask };
                              const bId = typeof created.bucket === 'object' ? created.bucket._id : created.bucket;
                              const updatedBuckets = { ...prev.buckets };
                              if (updatedBuckets[bId] && !updatedBuckets[bId].taskIds.includes(created._id)) {
                                updatedBuckets[bId] = {
                                  ...updatedBuckets[bId],
                                  taskIds: insertTaskSorted(updatedBuckets[bId].taskIds, created._id, updatedTasks),
                                };
                              }
                              return { ...prev, tasks: updatedTasks, buckets: updatedBuckets };
                            });
                            e.target.value = '';
                            setShowInput((prev) => ({ ...prev, [bucket.id]: false }));
                          } catch (error) {
                            console.error('Error adding ticket:', error);
                          }
                        }
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '5px',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '14px',
                      marginTop: '10px',
                    }}
                  />
                )}
                </div>
              </Droppable>
              </div>
            );
          })}
        </div>
      </DndContext>
    </div>
  );
}

export default memo(TicketBoard);
